// src/pages/LandingPage.js
import { Box, Grid, Typography, Paper } from '@mui/material';
import UserLogo from '../images/UserLogoMock.jpg'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMyUserProfile } from '../APIs/UsersAPI';
import { getAllTavoli } from '../APIs/TablesAPI';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getAllStanze } from '../APIs/RoomsAPI';
import { SetupWSEventListener } from '../utils/setupWSEventListener'; 
import { getTenantTranslation } from '../utils/getTenantTranslation';
import meetingAreaPurpleIcon from '../assets/icons/meeting-area-purple.png';
import spaLogoWhite from '../assets/icons/spa-logo-white.png';
import RestaurantIcon from '@mui/icons-material/Restaurant';

const getTableStatus = (table) => String(table?.status || '').toUpperCase();
const isFreeTable = (table) => getTableStatus(table) === 'LIBERO';
const isSpaCabinName = (name) => /^cab(?:in)?\s*\d+/i.test(String(name || '').trim());
const hasChildCabins = (room, tables = []) => {
    const roomId = Number(room?.id);
    if (Array.isArray(room?.tables) && room.tables.length > 0) return true;
    return tables.some((item) => Number(item?.roomId) === roomId);
};

const LandingPage = () => {
    const [user, setUser] = useState([]);
    const [table, setTable] = useState([]);
    const [userRole, setUserRole] = useState('');
    const [rooms, setRooms] = useState([]);
    const { t } = useTranslation();
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');
    const tenantType = localStorage.getItem('tenantType');
    const normalizedTenantType = String(tenantType || '').toUpperCase();
    const useMeetingIcons = normalizedTenantType === 'SALE_CONGRESSI';
    const useSpaIcons = normalizedTenantType === 'SPA';
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        try {
            const [profile, tableData, roomsData] = await Promise.all([
                getMyUserProfile(token, tenant),
                getAllTavoli(token, tenant),
                getAllStanze(token, tenant, { noCache: true }),
            ]);
            setUser(profile?.user || {});
            setUserRole(profile?.role?.name || '');
            setTable(tableData);
            setRooms(roomsData);
        } catch (error) {
            console.error(t('Errore:'), error);
        }
    }, [t, tenant, token])

    useEffect(() => {
        fetchData();
        const cleanup = SetupWSEventListener(fetchData);
        return cleanup;
    }, [fetchData])

    const activeRooms = useMemo(
        () => rooms.filter((room) => {
            if (room?.active === false) return false;
            if (normalizedTenantType !== 'SPA') return true;
            return hasChildCabins(room, table) && !isSpaCabinName(room?.name);
        }),
        [normalizedTenantType, rooms, table]
    );


    return (
        <Box sx={{
            mx: 'auto',
            width: '100%',
            maxWidth: 1180,
            p: { xs: 2, sm: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, md: 4 },
        }}
        >
            {/* Header */}
            <Box textAlign="center" mb={{ xs: 1, md: 4 }} marginTop={{ xs: 1, md: "1%" }}>
                <Box
                    component="img"
                    src={UserLogo}
                    alt=""
                    sx={{
                        width: { xs: 52, md: 64 },
                        height: { xs: 52, md: 64 },
                        borderRadius: '100%',
                    }}
                />
                <Typography variant="h4" fontWeight="bold" mt={'16px'}
                    sx={{
                        fontVariantNumeric: 'lining-nums proportional-nums',
                        fontFamily: 'Futura, sans-serif',
                        fontSize: { xs: '32px', sm: '36px', md: '40px' },
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '130%',
                        letterSpacing: 0,
                        overflowWrap: 'anywhere',
                    }}>
                    {t("hello")} {user.name}!
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 360, mx: 'auto' }}>
                    {t("work_selection")}
                </Typography>
                <Typography variant="caption"
                    sx={{
                        fontVariantNumeric: 'lining-nums proportional-nums',
                        fontFamily: "Helvetica Neue",
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '18.2px',
                        letterSpacing: 0,
                        display: 'block',
                        overflowWrap: 'anywhere',
                    }}>
                    {t("operator")} {tenant} – {userRole}
                </Typography>
            </Box>

            {/* Aree card */}
            <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
                {activeRooms.map((room) => {
                    const tavoliInStanza = table.filter(t => t.roomId === room.id);
                    const tavoliLiberi = tavoliInStanza.filter(isFreeTable);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={room.id}>
                            <Paper
                                onClick={() => {
                                    localStorage.setItem('selectedStanzaId', room.id);
                                    navigate(useMeetingIcons ? '/prenotazione' : '/home')
                                }}
                                elevation={4}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    cursor: "pointer",
                                    minHeight: { xs: 220, sm: 260, md: 302 },
                                    height: '100%',
                                    padding: { xs: 2.5, md: '33px' },
                                    background: 'linear-gradient(90deg, #9333EA 0%, #7E22CE 100%)',
                                    borderRadius: 2,
                                    color: '#fff',
                                }}
                            >
                                <Grid container
                                    sx={{
                                        height: "100%",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Grid item
                                        sx={{
                                            height: "50%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                paddingLeft: '29px',
                                                paddingRight: '29px',
                                            }}
                                        >
                                            <Box sx={{ lineHeight: 1, alignSelf: 'stretch' }}>
                                                {useMeetingIcons || useSpaIcons ? (
                                                    <Box
                                                        component="img"
                                                        src={useSpaIcons ? spaLogoWhite : meetingAreaPurpleIcon}
                                                        alt=""
                                                        aria-hidden="true"
                                                        sx={{
                                                            width: useSpaIcons ? { xs: 82, md: 96 } : { xs: 72, md: 88 },
                                                            height: useSpaIcons ? { xs: 70, md: 84 } : { xs: 72, md: 88 },
                                                            objectFit: 'contain',
                                                            display: 'block',
                                                            mx: 'auto',
                                                            filter: useSpaIcons ? 'drop-shadow(0 5px 14px rgba(0,0,0,0.28))' : 'none',
                                                        }}
                                                    />
                                                ) : (
                                                    <RestaurantIcon sx={{ width: '52.5px', height: '60px', color: 'white' }} />
                                                )}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                fontSize={{ xs: '28px', md: '30px' }}
                                                lineHeight={1.2}
                                                alignSelf={'stretch'}
                                                m={0}
                                                sx={{ overflowWrap: 'anywhere' }}
                                            >
                                                {room.name}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid
                                        item
                                        sx={{
                                            mt: 3,
                                            width: '100%',
                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            padding: '12px',
                                            textAlign: 'left',
                                            maxWidth: 220,
                                            mx: 'auto',
                                        }}
                                    >
                                        <Typography variant="caption"
                                            sx={{
                                                display: 'flex',
                                                fontSize: '14px',
                                            }}>
                                            {getTenantTranslation(t, tenantType, 'activeTables')}: {tavoliInStanza.length}
                                        </Typography>
                                        <Typography variant="caption"
                                            sx={{
                                                display: 'flex',
                                                fontSize: '14px',
                                            }}>
                                            {getTenantTranslation(t, tenantType, 'freeTables')}: {tavoliLiberi.length}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    )
                })}
            </Grid>
            <Box display={'flex'} alignSelf={'center'} sx={{ width: '100%', justifyContent: 'center' }}>
                <Paper
                    elevation={3}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '25px',
                        width: '100%',
                        maxWidth: '770px',
                        alignSelf: 'center',
                        borderRadius: 2,
                        mt: 3,
                    }}
                >
                    {/* Titolo */}
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: 18 }}>
                        {t("generalstatus")}
                    </Typography>

                    {/* Stato delle aree */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: { xs: 2, md: 6 },
                            width: '100%',
                            flexWrap: 'wrap',
                        }}
                    >
                        {activeRooms.map((room) => {
                            const isActive = room?.active !== false;

                            return (
                                <Box
                                    key={room.id}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Box
                                            sx={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: isActive ? '#22C55E' : '#EF4444',
                                            }}
                                        />
                                        <Typography fontSize={14} fontWeight={400} sx={{ overflowWrap: 'anywhere' }}>{room.name}</Typography>
                                    </Box>
                                    <Typography fontWeight={700} fontSize={18}>
                                        {isActive ? t("active") : t("Inactive")}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default LandingPage;
